import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
  Plus,
  Upload,
  Eye,
  EyeOff,
  Link as LinkIcon,
  Instagram,
  Facebook,
  Youtube,
  MessageCircle,
  Music2,
  Mail,
  Globe,
  Ticket,
  Send,
  Calendar,
  Sparkles,
  ShoppingBag,
  Phone,
  MapPin,
  Users,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { API_URL } from "../../config";

export const LINK_ICONS = {
  link: LinkIcon,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  whatsapp: MessageCircle,
  tiktok: Music2,
  email: Mail,
  website: Globe,
  ticket: Ticket,
  send: Send,
  calendar: Calendar,
  sparkles: Sparkles,
  shop: ShoppingBag,
  phone: Phone,
  location: MapPin,
  users: Users,
};

const ICON_OPTIONS = Object.keys(LINK_ICONS);

const uid = () => Math.random().toString(36).slice(2, 10);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 bg-black/40 rounded-xl px-3 py-2.5 border border-white/5 cursor-pointer hover:border-white/20 transition-colors">
    <Switch
      checked={!!checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-amber-500"
      data-testid={testid}
    />
    <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider">
      {label}
    </span>
  </label>
);

export const InactiveLandingSettings = ({ show, setShow, onSave, saving }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // link id being edited (null=none, 'new'=new link)
  const [linkDraft, setLinkDraft] = useState({
    id: "",
    title: "",
    subtitle: "",
    url: "",
    icon: "link",
    hidden: false,
  });

  const links = Array.isArray(show?.inactive_landing_links)
    ? show.inactive_landing_links
    : [];

  const patch = (partial) => setShow({ ...show, ...partial });

  const setLinks = (next) => patch({ inactive_landing_links: next });

  const startNewLink = () => {
    setLinkDraft({
      id: uid(),
      title: "",
      subtitle: "",
      url: "",
      icon: "link",
      hidden: false,
    });
    setEditing("new");
  };

  const startEditLink = (link) => {
    setLinkDraft({ ...link });
    setEditing(link.id);
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const saveLink = () => {
    if (!linkDraft.title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    if (!linkDraft.url.trim()) {
      toast.error("URL é obrigatório");
      return;
    }
    let next;
    if (editing === "new") {
      next = [...links, linkDraft];
    } else {
      next = links.map((l) => (l.id === editing ? linkDraft : l));
    }
    setLinks(next);
    setEditing(null);
    toast.success("Link guardado. Não esqueça de guardar as alterações.");
  };

  const removeLink = (id) => {
    if (!window.confirm("Remover este link?")) return;
    setLinks(links.filter((l) => l.id !== id));
  };

  const toggleHidden = (id) => {
    setLinks(links.map((l) => (l.id === id ? { ...l, hidden: !l.hidden } : l)));
  };

  const move = (id, dir) => {
    const idx = links.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= links.length) return;
    const next = [...links];
    [next[idx], next[j]] = [next[j], next[idx]];
    setLinks(next);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/inactive-landing/image`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      patch({ inactive_landing_image: res.data.url });
      toast.success("Imagem carregada. Guarde as alterações.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewInactiveLanding = () => {
    // Open landing in new tab with force-preview flag
    window.open("/?inactive_preview=1", "_blank");
  };

  const openCheckout = () => {
    // Bypass the alternate landing and open the real registration page
    window.open("/?checkout=1", "_blank");
  };

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
      <div className="flex items-start gap-4 mb-6">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white uppercase tracking-wide">
            Página de Entrada
          </h3>
          <p className="text-zinc-500 text-sm mt-1">
            Página bio-link (estilo Linktree) mostrada aos visitantes quando activa. Tudo
            é editável aqui — nome do evento, imagem, textos, links e rodapé.
          </p>
        </div>
      </div>

      {/* Master toggle */}
      <div className="bg-black/40 rounded-xl p-4 border border-amber-500/40 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Switch
              checked={!!show?.inactive_landing_enabled}
              onCheckedChange={(v) => patch({ inactive_landing_enabled: v })}
              className="data-[state=checked]:bg-amber-500"
              data-testid="toggle-inactive-landing-enabled"
            />
            <div>
              <div className="text-white font-semibold uppercase tracking-wider text-sm">
                Mostrar a Página de Entrada
              </div>
              <div className="text-zinc-500 text-xs mt-0.5">
                Activo: quem visita sem sessão vê esta página. Inactivo: vê directamente
                o fluxo normal de compra e o login.
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={previewInactiveLanding}
            className="border-white/10 text-zinc-300 hover:bg-white/5"
            data-testid="preview-inactive-landing-btn"
          >
            Pré-visualizar
          </Button>
        </div>
      </div>

      {/* Quick access to the real registration page (bypasses the alt landing) */}
      <div className="bg-black/30 rounded-xl p-4 border border-white/5 mb-6 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-white font-semibold uppercase tracking-wider text-sm">
            Página de Inscrições
          </div>
          <div className="text-zinc-500 text-xs mt-0.5">
            Abra a página real de compra de bilhetes, mesmo com a Página de Entrada activa.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={openCheckout}
          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 shrink-0"
          data-testid="open-checkout-btn"
        >
          <LinkIcon className="w-4 h-4 mr-2" />
          Abrir Inscrições
        </Button>
      </div>

      {/* Visibility toggles */}
      <div className="bg-black/30 rounded-xl p-5 border border-white/5 mb-6">
        <div className="mb-3">
          <div className="text-white font-semibold uppercase tracking-wider text-sm">
            Mostrar ou ocultar cada parte
          </div>
          <div className="text-zinc-500 text-xs mt-0.5">
            Desligue o que não quer mostrar. O conteúdo fica guardado e volta a aparecer
            quando ligar outra vez.
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <Toggle label="Imagem de Fundo" checked={show?.inactive_landing_show_image} onChange={(v) => patch({ inactive_landing_show_image: v })} testid="toggle-il-image" />
          <Toggle label="Nome do Evento" checked={show?.inactive_landing_show_name} onChange={(v) => patch({ inactive_landing_show_name: v })} testid="toggle-il-name" />
          <Toggle label="Texto Pequeno" checked={show?.inactive_landing_show_small_text} onChange={(v) => patch({ inactive_landing_show_small_text: v })} testid="toggle-il-small" />
          <Toggle label="Título Principal" checked={show?.inactive_landing_show_title} onChange={(v) => patch({ inactive_landing_show_title: v })} testid="toggle-il-title" />
          <Toggle label="Mensagem" checked={show?.inactive_landing_show_message} onChange={(v) => patch({ inactive_landing_show_message: v })} testid="toggle-il-message" />
          <Toggle label="Datas" checked={show?.inactive_landing_show_dates} onChange={(v) => patch({ inactive_landing_show_dates: v })} testid="toggle-il-dates" />
          <Toggle label="Lista de Links" checked={show?.inactive_landing_show_links} onChange={(v) => patch({ inactive_landing_show_links: v })} testid="toggle-il-links" />
          <Toggle label="Redes Sociais" checked={show?.inactive_landing_show_social} onChange={(v) => patch({ inactive_landing_show_social: v })} testid="toggle-il-social" />
          <Toggle label="Rodapé" checked={show?.inactive_landing_show_footer} onChange={(v) => patch({ inactive_landing_show_footer: v })} testid="toggle-il-footer" />
        </div>
      </div>

      {/* Admin button */}
      <div className="bg-black/30 rounded-xl p-5 border border-white/5 mb-6">
        <div className="mb-4">
          <div className="text-white font-semibold uppercase tracking-wider text-sm">
            Botão "Admin" (Acesso Discreto)
          </div>
          <div className="text-zinc-500 text-xs mt-0.5">
            Controle a apresentação do link discreto de acesso à administração.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 bg-black/40 rounded-xl px-3 py-3 border border-white/5">
            <Switch
              checked={show?.inactive_landing_admin_button_visible !== false}
              onCheckedChange={(v) => patch({ inactive_landing_admin_button_visible: v })}
              className="data-[state=checked]:bg-amber-500"
              data-testid="toggle-il-admin-btn"
            />
            <div>
              <div className="text-[12px] font-semibold text-zinc-300 uppercase tracking-wider">Botão Visível</div>
              <div className="text-[11px] text-zinc-500">Mostra ou oculta o pequeno link discreto no rodapé.</div>
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Texto do Botão</Label>
            <Input
              value={show?.inactive_landing_admin_button_text ?? "Admin"}
              onChange={(e) => patch({ inactive_landing_admin_button_text: e.target.value })}
              className="input-dark mt-2"
              placeholder="Admin"
              data-testid="input-il-admin-btn-text"
            />
          </div>
        </div>
      </div>

      {/* Content fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Nome do Evento (badge)</Label>
          <Input
            value={show?.inactive_landing_name ?? ""}
            onChange={(e) => patch({ inactive_landing_name: e.target.value })}
            className="input-dark mt-2"
            placeholder={show?.name || "Ex: ACAMP 2026"}
            data-testid="input-il-name"
          />
          <p className="text-[11px] text-zinc-500 mt-1">Deixe vazio para usar o nome principal ({show?.name || "—"}).</p>
        </div>
        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Texto Pequeno</Label>
          <Input
            value={show?.inactive_landing_small_text ?? ""}
            onChange={(e) => patch({ inactive_landing_small_text: e.target.value })}
            className="input-dark mt-2"
            placeholder="Ex: Até ao próximo evento."
            data-testid="input-il-small"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Título Principal</Label>
          <Input
            value={show?.inactive_landing_title ?? ""}
            onChange={(e) => patch({ inactive_landing_title: e.target.value })}
            className="input-dark mt-2"
            placeholder="Ex: XTREMO"
            data-testid="input-il-title"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Mensagem</Label>
          <Textarea
            value={show?.inactive_landing_message ?? ""}
            onChange={(e) => patch({ inactive_landing_message: e.target.value })}
            className="input-dark mt-2 min-h-[80px]"
            placeholder="Ex: Estamos a preparar a próxima edição. Volta em breve para novidades e inscrições."
            data-testid="input-il-message"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Datas do Próximo Evento</Label>
          <Input
            value={show?.inactive_landing_dates ?? ""}
            onChange={(e) => patch({ inactive_landing_dates: e.target.value })}
            className="input-dark mt-2"
            placeholder="Ex: 15 – 22 de Julho de 2026"
            data-testid="input-il-dates"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Formato da Imagem</Label>
          <Select
            value={show?.inactive_landing_image_fit ?? "cover"}
            onValueChange={(v) => patch({ inactive_landing_image_fit: v })}
          >
            <SelectTrigger className="input-dark mt-2" data-testid="select-il-image-fit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cover">Cover — preenche o ecrã, corta o que sobrar</SelectItem>
              <SelectItem value="contain">Inteira — mostra a imagem completa, com margens</SelectItem>
              <SelectItem value="full">Cabeçalho — apenas no topo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Imagem — URL ou Upload</Label>
          <div className="flex flex-col md:flex-row gap-2 mt-2">
            <Input
              value={show?.inactive_landing_image ?? ""}
              onChange={(e) => patch({ inactive_landing_image: e.target.value })}
              className="input-dark flex-1"
              placeholder="https://... ou faça upload"
              data-testid="input-il-image"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="border-white/10 text-zinc-300 hover:bg-white/5"
              data-testid="btn-il-upload"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "A carregar..." : "Upload"}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Qualquer formato: quadrada, vertical ou 1920×1080. Máx. 6MB.
          </p>
          {show?.inactive_landing_image && (
            <div className="mt-3 rounded-lg overflow-hidden border border-white/10 bg-black/40 max-w-xs">
              <img
                src={show.inactive_landing_image}
                alt="Pré-visualização"
                className="w-full h-32 object-cover"
                onError={(e) => (e.currentTarget.style.opacity = "0.3")}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer overrides */}
      <div className="bg-black/30 rounded-xl p-5 border border-white/5 mb-6">
        <div className="mb-4">
          <div className="text-white font-semibold uppercase tracking-wider text-sm">
            Rodapé
          </div>
          <div className="text-zinc-500 text-xs mt-0.5">
            Personalize o texto do rodapé apenas nesta página. Deixe vazio para usar o
            rodapé definido nas configurações principais.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Rodapé Principal (linha 1)</Label>
            <Input
              value={show?.inactive_landing_footer_text ?? ""}
              onChange={(e) => patch({ inactive_landing_footer_text: e.target.value })}
              className="input-dark mt-2"
              placeholder={show?.footer_text || "Todos os direitos reservados"}
              data-testid="input-il-footer-text"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Rodapé Secundário (linha 2)</Label>
            <Input
              value={show?.inactive_landing_footer_copyright ?? ""}
              onChange={(e) => patch({ inactive_landing_footer_copyright: e.target.value })}
              className="input-dark mt-2"
              placeholder={show?.footer_copyright || "Em caso de dúvida..."}
              data-testid="input-il-footer-copyright"
            />
          </div>
        </div>
      </div>

      {/* Links */}
      <div className="bg-black/30 rounded-xl p-5 border border-white/5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-white font-semibold uppercase tracking-wider text-sm">
              Links da Página
            </div>
            <div className="text-zinc-500 text-xs mt-0.5">
              Adicione quantos links quiser: inscrições, site, contactos, vídeos, formulários ou redes.
            </div>
          </div>
          <Button
            size="sm"
            onClick={startNewLink}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="btn-il-add-link"
          >
            <Plus className="w-4 h-4 mr-1" /> Adicionar Link
          </Button>
        </div>

        {/* Editor */}
        {editing !== null && (
          <div className="bg-black/60 rounded-lg p-4 border border-amber-500/40 mb-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Título</Label>
                <Input
                  value={linkDraft.title}
                  onChange={(e) => setLinkDraft({ ...linkDraft, title: e.target.value })}
                  className="input-dark mt-1.5"
                  placeholder="Ex: BILHETES — Verão 2026"
                  data-testid="input-link-title"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Ícone</Label>
                <Select
                  value={linkDraft.icon}
                  onValueChange={(v) => setLinkDraft({ ...linkDraft, icon: v })}
                >
                  <SelectTrigger className="input-dark mt-1.5" data-testid="select-link-icon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((k) => {
                      const Icon = LINK_ICONS[k];
                      return (
                        <SelectItem key={k} value={k}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            <span className="capitalize">{k}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Subtítulo (opcional)</Label>
                <Input
                  value={linkDraft.subtitle}
                  onChange={(e) => setLinkDraft({ ...linkDraft, subtitle: e.target.value })}
                  className="input-dark mt-1.5"
                  placeholder="Ex: Acede à plataforma para comprares os teus bilhetes"
                  data-testid="input-link-subtitle"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">URL</Label>
                <Input
                  value={linkDraft.url}
                  onChange={(e) => setLinkDraft({ ...linkDraft, url: e.target.value })}
                  className="input-dark mt-1.5"
                  placeholder="https://..."
                  data-testid="input-link-url"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={saveLink} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="btn-link-save">
                Guardar Link
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-zinc-400">
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="space-y-2">
          {links.length === 0 && editing === null && (
            <div className="text-center py-10 text-zinc-500 text-sm">
              Nenhum link adicionado ainda.
            </div>
          )}
          {links.map((l, idx) => {
            const Icon = LINK_ICONS[l.icon] || LinkIcon;
            return (
              <div
                key={l.id}
                className={`flex items-center gap-3 bg-black/40 rounded-lg p-3 border border-white/5 ${
                  l.hidden ? "opacity-50" : ""
                }`}
              >
                <div className="w-9 h-9 rounded-md bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold truncate uppercase tracking-wide">
                    {l.title || "(sem título)"}
                  </div>
                  {l.subtitle && (
                    <div className="text-zinc-400 text-xs truncate">{l.subtitle}</div>
                  )}
                  <div className="text-zinc-500 text-[11px] truncate">{l.url}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => move(l.id, "up")} disabled={idx === 0} className="h-8 w-8 text-zinc-400 hover:text-white">
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => move(l.id, "down")} disabled={idx === links.length - 1} className="h-8 w-8 text-zinc-400 hover:text-white">
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => toggleHidden(l.id)} className="h-8 w-8 text-zinc-400 hover:text-white" title={l.hidden ? "Mostrar" : "Ocultar"}>
                    {l.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => startEditLink(l)} className="h-8 w-8 text-zinc-400 hover:text-white">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => removeLink(l.id)} className="h-8 w-8 text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Button
        onClick={onSave}
        disabled={saving}
        className="btn-primary"
        data-testid="save-inactive-landing-btn"
      >
        {saving ? "A guardar..." : "Guardar Alterações"}
      </Button>
    </div>
  );
};

export default InactiveLandingSettings;
